import { Box, Tab, Tabs } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LabDashboard from "./dashboard";
import UserReport from "./userReport";
import PaymentSummary from "./paymentSummary";
import Transaction from "./transaction";
import IncomeExpenseSummaryTab from "../../components/IncomeExpenseSummary";
import TestCountReport from "./testCountReport";
import StockLedger from "./stockLedger";
import DoctorShares from "./doctorShareSummary";
import DoctorShareSummaryReport from "./doctorShareSummaryReport";

const Lab = () => {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const labPermissions = profile?.data?.accessConfigurations?.Lab;

  // Define All Available Tabs
  const allTabs = useMemo(() => [
    { 
      label: "Dashboard", 
      path: "dashboard", 
      component: <LabDashboard />,
      permissionKey: null // Always show dashboard or handle separately if needed
    },
    { 
      label: "User Wise Registrations", 
      path: "user-reports", 
      component: <UserReport />,
      permissionKey: null 
    },
    { 
      label: "Payment Summary", 
      path: "payment-summary", 
      component: <PaymentSummary />,
      permissionKey: "payment_summary"
    },
    { 
      label: "Transaction", 
      path: "transaction", 
      component: <Transaction />,
      permissionKey: "transaction"
    },
    { 
      label: "Income & Expense Summary", 
      path: "income-expense-summary", 
      component: <IncomeExpenseSummaryTab />,
      permissionKey: "income_expense_summary"
    },
    {
      label: "Test Count Report",
      path: "test-count-report",
      component: <TestCountReport />,
      permissionKey: "test_count_report",
      requireExplicitPermission: true,
    },
    {
      label: "Stock Ledger",
      path: "stock-ledger",
      component: <StockLedger />,
      permissionKey: "stock_ledger"
    },
    {
      label: "Doctor Shares",
      path: "doctor-shares",
      component: <DoctorShares />,
      permissionKey: "doctor_share_summary",
    },
    {
      label: "Doctor Share Summary",
      path: "doctor-share-summary",
      component: <DoctorShareSummaryReport />,
      permissionKey: "doctor_share_summary",
    },
  ], []);

  // Filter tabs based on permissions
  const filteredTabs = useMemo(() => {
    return allTabs.filter(tab => {
      if (!tab.permissionKey) return true;
      if ((tab as any).requireExplicitPermission) {
        return labPermissions?.[tab.permissionKey] === true;
      }
      // If permission is explicitly false, hide it. Otherwise show (true or undefined)
      return labPermissions?.[tab.permissionKey] !== false;
    });
  }, [allTabs, labPermissions]);

  // Set active tab based on current URL
  useEffect(() => {
    const currentPath = window.location.pathname.split("/").pop();
    const matchedTabIndex = filteredTabs.findIndex(tab => tab.path === currentPath);
    if (matchedTabIndex !== -1) {
      setActiveTab(matchedTabIndex);
    }
  }, [filteredTabs]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = filteredTabs[newValue];
    if (selectedTab) {
      navigate(`/mylab/${selectedTab.path}`);
    }
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "white",
          borderRadius: "4px 4px 0 0",
          px: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="account dashboard tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {filteredTabs.map((tab, idx) => (
            <Tab
              key={tab.path}
              sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
              label={tab.label}
            />
          ))}
        </Tabs>
      </Box>

      {filteredTabs[activeTab] && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          {filteredTabs[activeTab].component}
        </Box>
      )}
    </Box>
  );
};
export default Lab;
